import { ACCESS_KEY, urlAPI, urlAPIv2 } from '../../../urls'
import { uploadStrapiFile, transcodeImage } from '../../../store/actionsCreators/fileActions'
import type { SponsoredPartnersRemoteConfig } from './sponsoredPartners.types'

export const DEFAULT_SPONSORED_PARTNERS_CONFIG: SponsoredPartnersRemoteConfig = {
    version: 1,
    partners: []
}

export const normalizeSponsoredPartnersConfig = (value: unknown): SponsoredPartnersRemoteConfig => {
    if (value == null || typeof value !== 'object') return DEFAULT_SPONSORED_PARTNERS_CONFIG
    const raw = value as Partial<SponsoredPartnersRemoteConfig>

    return {
        version: 1,
        ...(typeof raw.updatedAt === 'string' ? { updatedAt: raw.updatedAt } : {}),
        partners: Array.isArray(raw.partners) ? raw.partners : []
    }
}

const getAuthHeaders = (): HeadersInit => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${window.localStorage.getItem(ACCESS_KEY) ?? ''}`
})

export const fetchConfigRecord = async (): Promise<any> => {
    const v2Response = await fetch(`${urlAPIv2}/config`, {
        method: 'GET',
        headers: getAuthHeaders()
    })

    if (v2Response.ok) {
        return await v2Response.json()
    }

    const legacyResponse = await fetch(urlAPI, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            method: 'GET',
            collection: 'config'
        })
    })
    const legacyJson = await legacyResponse.json()

    if (!legacyResponse.ok || legacyJson?.success !== true || legacyJson?.response?.error != null) {
        throw new Error(JSON.stringify(legacyJson?.response?.error ?? legacyJson))
    }

    return legacyJson.response
}

export const getConfigAttributes = (record: any): Record<string, any> => (
    record?.data?.attributes ??
    record?.response?.data?.attributes ??
    record?.attributes ??
    {}
)

export const fetchSponsoredPartnersConfig = async (): Promise<SponsoredPartnersRemoteConfig> => {
    const record = await fetchConfigRecord()
    const attrs = getConfigAttributes(record)
    return normalizeSponsoredPartnersConfig(attrs?.SponsoredPartners)
}

export const buildSponsoredPartnersSavePayload = (partners: SponsoredPartnersRemoteConfig['partners']) => ({
    data: {
        SponsoredPartners: {
            version: 1 as const,
            updatedAt: new Date().toISOString(),
            partners
        }
    }
})

export const saveSponsoredPartnersConfig = async (
    partners: SponsoredPartnersRemoteConfig['partners']
): Promise<{ config: SponsoredPartnersRemoteConfig, requestPayload: ReturnType<typeof buildSponsoredPartnersSavePayload> }> => {
    await fetchConfigRecord()
    const requestPayload = buildSponsoredPartnersSavePayload(partners)

    const response = await fetch(`${urlAPIv2}/config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestPayload)
    })

    const text = await response.text()
    let json: any = null
    try {
        json = text ? JSON.parse(text) : null
    } catch {
        throw new Error(`Non-JSON Config response (${response.status}): ${text}`)
    }

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${JSON.stringify(json)}`)
    }

    return {
        config: requestPayload.data.SponsoredPartners,
        requestPayload
    }
}

export const uploadSponsoredPartnerImage = async (file: File): Promise<string> => {
    const compressed = file.type.startsWith('image/') ? await transcodeImage(file) : null
    const uploaded = await uploadStrapiFile(compressed ?? file, 'chat-rooms')
    const url = uploaded?.data

    if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
        throw new Error('Upload finished, but the returned storage URL is invalid.')
    }

    return url
}
